import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ExternalLink, Mail, Plus, Save, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${window.location.origin}/api/budget`;

/** BB-ADMIN — Budgets settings (Settings › Budgets). Nothing here is a second copy: the disclaimer and the budget
 *  qualifications live in the qualification store (BB-FIX-6), the subject aliases (the one editor)
 *  are BB-FIX-7's table, and email is owned by Settings › Email — this page links to it. */
export default function AdminSettings() {
  const [quals, setQuals] = useState(null);
  const [disclaimer, setDisclaimer] = useState("");
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    // Lives in the master Settings shell now, outside BudgetApp — attach the bearer token the Budgets module would.
    const t = localStorage.getItem("auth_token");
    if (t) axios.defaults.headers.common["Authorization"] = `Bearer ${t}`;
    (async () => {
      try {
        const [q, e] = await Promise.all([
          axios.get(`${API}/admin/qualifications`),
          axios.get(`${API}/admin/email-settings`).catch(() => ({ data: null })),
        ]);
        setQuals(q.data); setDisclaimer(q.data.disclaimer || ""); setRows(q.data.qualifications || []);
        setEmail(e.data);
      } catch (err) { toast.error(err.response?.data?.detail || "Could not load settings"); }
    })();
  }, []);

  const saveQuals = async () => {
    setBusy("quals");
    try {
      const { data } = await axios.put(`${API}/admin/qualifications`, {
        disclaimer,
        qualifications: rows.map((r) => ({ id: r.id, text: r.text, usage: r.usage || "always", scope_tags: r.scope_tags || [], active: r.active !== false })),
      });
      setQuals(data); setRows(data.qualifications || []);
      toast.success("Saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setBusy(""); }
  };

  if (!quals) return <div className="p-6 text-sm text-slate-500">Loading…</div>;
  const Section = ({ title, sub, children, testId, action }) => (
    <div className="bg-card text-card-foreground border border-border rounded-sm" data-testid={testId}>
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border">
        <div>
          <h2 className="text-sm font-semibold">{title}</h2>
          {sub && <p className="text-xs text-slate-500">{sub}</p>}
        </div>
        <div className="ml-auto">{action}</div>
      </div>
      <div className="p-4 space-y-3">{children}</div>
    </div>
  );

  return (
    <div className="page-body space-y-6" data-testid="bb-admin-settings">
      <Section title="Proposal disclaimer" testId="settings-disclaimer"
               sub="The global line printed on every budget summary and proposal."
               action={<Button size="sm" disabled={busy === "quals"} onClick={saveQuals} data-testid="settings-save-quals"><Save className="w-3.5 h-3.5 mr-1" />Save</Button>}>
        <Textarea value={disclaimer} onChange={(e) => setDisclaimer(e.target.value)} rows={3}
                  className="text-sm" data-testid="settings-disclaimer-input" />
        <div className="text-[11px] text-slate-400">{disclaimer.length}/2000</div>
      </Section>

      <Section title="Budget qualifications" testId="settings-qualifications"
               sub={`One store with the rest of the app — ${quals.library_count} lines in the qualification library. "Always" prints on every budget; "menu" is offered per scope.`}
               action={<Button size="sm" variant="outline" onClick={() => setRows((r) => [...r, { id: "", text: "", usage: "always", active: true }])} data-testid="settings-add-qual"><Plus className="w-3.5 h-3.5 mr-1" />Add</Button>}>
        {rows.map((r, i) => (
          <div key={r.id || i} className="flex items-center gap-2" data-testid={`settings-qual-${i}`}>
            <Input value={r.text} onChange={(e) => setRows((x) => x.map((y, j) => (j === i ? { ...y, text: e.target.value } : y)))}
                   className="h-8 text-sm flex-1" data-testid={`settings-qual-text-${i}`} />
            <select value={r.usage || "always"} onChange={(e) => setRows((x) => x.map((y, j) => (j === i ? { ...y, usage: e.target.value } : y)))}
                    className="h-8 text-xs border border-border rounded-sm px-1 bg-transparent" data-testid={`settings-qual-usage-${i}`}>
              <option value="always">always</option>
              <option value="menu">menu</option>
            </select>
            <button onClick={() => setRows((x) => x.filter((_, j) => j !== i))} className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`settings-qual-remove-${i}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        <Button size="sm" disabled={busy === "quals"} onClick={saveQuals} data-testid="settings-save-quals-2"><Save className="w-3.5 h-3.5 mr-1" />Save qualifications</Button>
      </Section>


      <Section title="Email" testId="settings-email"
               sub="Every Budgets email goes through the app's email framework — templates, send-as identity, review before send and test recipients all live in Settings › Email.">
        <div className="text-xs space-y-1.5">
          <div>Sends as: <b>{email?.identity?.send_as_user ? "the signed-in user where the domain allows it" : email?.identity?.department_name || "the department address"}</b>
            {email?.identity?.department_email && <span className="text-slate-500"> · {email.identity.department_email}</span>}</div>
          {Object.entries(email?.templates || {}).map(([k, t]) => (
            <div key={k} className="flex items-center gap-2" data-testid={`settings-email-template-${k}`}>
              <Mail className="w-3.5 h-3.5 text-slate-400" />
              <span className="font-medium">{t.name}</span>
              <Badge className={`text-xs rounded-sm border ${t.review_before_send === false ? "bg-amber-50 text-amber-800 border-amber-100" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                {t.review_before_send === false ? "auto-send allowed" : "review before send"}
              </Badge>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => window.dispatchEvent(new CustomEvent("abs:navigate", { detail: { tab: "settings", sub: "email" } }))}
                className="inline-flex items-center gap-1 text-xs text-foreground underline hover:underline" data-testid="settings-email-link">
          Open Settings › Email <ExternalLink className="w-3 h-3" />
        </button>
        {(email?.recent || []).length > 0 && (
          <div className="text-xs text-slate-500">
            Last send: {email.recent[0].entity_name} · {new Date(email.recent[0].timestamp).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
          </div>
        )}
      </Section>
    </div>
  );
}
